/**
 * Markdown-it plugin for blockquote citations
 * @fileoverview The LAST line of a blockquote starting with `-- ` or `— `
 * becomes <cite class="cm-blockquote-cite">Author</cite>.
 *
 * GOTCHA: with breaks:true a citation usually shares its paragraph with the
 * quoted text via a softbreak, so token streams must be SPLIT — inspecting
 * whole paragraphs is not enough. Same approach as the markdown-editor
 * static renderer.
 */

// `-- `, `— ` (em dash) and `– ` (en dash — typographer turns `--` into
// `–` before this rule runs).
const CITATION_BODY_RE = /^(--\s*|—\s*|–\s*)(.*)/;

export default function blockquoteCitePlugin(md) {
  const citationCore = (state) => {
    const tokens = state.tokens;

    // Collect blockquote ranges [openIdx, closeIdx].
    const ranges = [];
    for (let i = 0; i < tokens.length; i++) {
      if (tokens[i].type !== 'blockquote_open') continue;
      let depth = 1;
      let j = i + 1;
      while (j < tokens.length && depth > 0) {
        if (tokens[j].type === 'blockquote_open') depth++;
        else if (tokens[j].type === 'blockquote_close') depth--;
        j++;
      }
      ranges.push([i, j - 1]);
      i = j - 1;
    }

    // Process in reverse so splices don't shift earlier indices.
    for (let r = ranges.length - 1; r >= 0; r--) {
      const [openIdx, closeIdx] = ranges[r];

      for (let o = closeIdx - 1; o > openIdx; o--) {
        if (tokens[o].type !== 'paragraph_open' || tokens[o].tag === 'cite') continue;
        const inlineTok = tokens[o + 1];
        if (!inlineTok || inlineTok.type !== 'inline' || !inlineTok.children?.length) continue;
        const children = inlineTok.children;

        // Case 1 — whole paragraph is the citation.
        const first = children[0];
        let m = CITATION_BODY_RE.exec(first?.content || '');
        if (first?.type === 'text' && m && m[2]) {
          first.content = m[2];
          tokens[o].tag = 'cite';
          tokens[o].attrJoin('class', 'cm-blockquote-cite');
          let k = o + 2;
          while (k < tokens.length && tokens[k].type !== 'paragraph_close') k++;
          if (k < tokens.length) tokens[k].tag = 'cite';
          continue;
        }

        // Case 2 — citation follows a softbreak inside the paragraph.
        for (let c = 0; c < children.length - 1; c++) {
          if (children[c].type !== 'softbreak') continue;
          const next = children[c + 1];
          m = CITATION_BODY_RE.exec(next?.content || '');
          if (!next || next.type !== 'text' || !m || !m[2]) continue;

          next.content = m[2];
          const before = children.slice(0, c); // drop the softbreak
          const after = children.slice(c + 1);
          inlineTok.children = before;
          inlineTok.content = before.map((x) => x.content || '').join('');

          const citeInline = new state.Token('inline', '', 0);
          citeInline.children = after;
          citeInline.content = after.map((x) => x.content || '').join('');

          const citeOpen = new state.Token('paragraph_open', 'cite', 1);
          citeOpen.attrJoin('class', 'cm-blockquote-cite');
          const citeClose = new state.Token('paragraph_close', 'cite', -1);

          // Split this paragraph into <p>…</p><cite>…</cite>.
          let k = o + 2;
          while (k < tokens.length && tokens[k].type !== 'paragraph_close') k++;
          tokens.splice(
            k,
            1,
            new state.Token('paragraph_close', tokens[o].tag || 'p', -1),
            citeOpen,
            citeInline,
            citeClose,
          );
          break;
        }
      }
    }
  };

  md.core.ruler.push('blockquote_citation', citationCore);

  // Paragraph renderer honours token.tag so citations emit <cite>.
  md.renderer.rules.paragraph_open = (tokens, idx, options, env, slf) =>
    `<${tokens[idx].tag}${slf.renderAttrs(tokens[idx])}>\n`;
  md.renderer.rules.paragraph_close = (tokens, idx) => `</${tokens[idx].tag}>\n`;
}
